import React from 'react';
import { AlertCircle, CheckCircle, Mail, Trash2, UserPlus, Users, XCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { TabsContent } from '../../../components/ui/tabs';

export const InvitesTab = ({ approvedRequests, deletingRequest, getStatusBadge, handleDeleteRequest, loadingRequests, managerRequests, pendingRequests, rejectedRequests }) => (
            <TabsContent value="invites">
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-yellow-200 bg-yellow-50/50">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="w-8 h-8 text-yellow-600" />
                        <div>
                          <p className="text-2xl font-bold text-yellow-600">{pendingRequests.length}</p>
                          <p className="text-sm text-muted-foreground">Pending Invites</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200 bg-green-50/50">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                        <div>
                          <p className="text-2xl font-bold text-green-600">{approvedRequests.length}</p>
                          <p className="text-sm text-muted-foreground">Approved</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <XCircle className="w-8 h-8 text-red-600" />
                        <div>
                          <p className="text-2xl font-bold text-red-600">{rejectedRequests.length}</p>
                          <p className="text-sm text-muted-foreground">Rejected</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
  
                {/* Pending Requests */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                      Pending Invites
                    </CardTitle>
                    <CardDescription>
                      Manager invitations waiting for approval
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingRequests ? (
                      <div className="text-center py-8 text-muted-foreground">Loading...</div>
                    ) : pendingRequests.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No pending invites</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {pendingRequests.map((req) => (
                          <div key={req.id} className="flex items-center justify-between p-4 border rounded-lg bg-yellow-50/30">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-medium">{req.requester_name}</p>
                                {getStatusBadge(req.status)}
                              </div>
                              <p className="text-sm text-muted-foreground">{req.requester_email}</p>
                              <p className="text-sm mt-1">
                                <span className="text-muted-foreground">Invited manager:</span>{' '}
                                <span className="font-medium">{req.manager_email}</span>
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {req.created_at ? new Date(req.created_at).toLocaleString() : 'N/A'}
                              </p>
                            </div>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteRequest(req.id)}
                              disabled={deletingRequest === req.id}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              {deletingRequest === req.id ? 'Deleting...' : 'Delete'}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
  
                {/* All Requests */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      All Manager Requests
                    </CardTitle>
                    <CardDescription>
                      Complete history of manager invitations
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingRequests ? (
                      <div className="text-center py-8 text-muted-foreground">Loading...</div>
                    ) : managerRequests.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No manager requests yet</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-3 px-2 font-medium">Requester</th>
                              <th className="text-left py-3 px-2 font-medium">Manager</th>
                              <th className="text-left py-3 px-2 font-medium">Status</th>
                              <th className="text-left py-3 px-2 font-medium">Date</th>
                              <th className="text-right py-3 px-2 font-medium">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {managerRequests.map((req) => (
                              <tr key={req.id} className="border-b last:border-0 hover:bg-slate-50">
                                <td className="py-3 px-2">
                                  <p className="font-medium">{req.requester_name}</p>
                                  <p className="text-xs text-muted-foreground">{req.requester_email}</p>
                                </td>
                                <td className="py-3 px-2">
                                  <p className="font-medium">{req.manager_name || '-'}</p>
                                  <p className="text-xs text-muted-foreground">{req.manager_email}</p>
                                </td>
                                <td className="py-3 px-2">
                                  {getStatusBadge(req.status)}
                                </td>
                                <td className="py-3 px-2 text-muted-foreground">
                                  {req.created_at ? new Date(req.created_at).toLocaleDateString() : 'N/A'}
                                </td>
                                <td className="py-3 px-2 text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteRequest(req.id)}
                                    disabled={deletingRequest === req.id}
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
);
